// AGC: Admin page for viewing waitlist signups
import React, { useState, useEffect, useMemo } from 'react'
import { collection, getDocs, orderBy, query } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import { useTranslation } from '../../hooks/useTranslation'
import { AdminTable, AdminThead, AdminTh, AdminTd, AdminTr } from './ui/AdminTable'
import { AdminPageHeader } from './ui/AdminPageHeader'
import { AdminBadge } from './ui/AdminBadge'
import { AdminButton } from './ui/AdminButton'
import { AdminLoadingState } from './ui/AdminLoadingState'
import { AdminEmptyState } from './ui/AdminEmptyState'
import { Download } from 'lucide-react'

// AGC: Shape of a waitlist Firestore document
interface WaitlistEntry {
  id: string
  product: string
  email: string
  userId: string
  userName: string
  createdAt: { toDate: () => Date } | null
}

export const WaitlistAdmin: React.FC = () => {
  const { t } = useTranslation()
  const [entries, setEntries] = useState<WaitlistEntry[]>([])
  const [loading, setLoading] = useState(true)

  // AGC: Fetch all waitlist entries ordered by signup date descending
  useEffect(() => {
    const fetchWaitlist = async () => {
      try {
        const q = query(collection(db, 'waitlist'), orderBy('createdAt', 'desc'))
        const snapshot = await getDocs(q)
        setEntries(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as WaitlistEntry)))
      } catch {
        setEntries([])
      } finally {
        setLoading(false)
      }
    }
    fetchWaitlist()
  }, [])

  // AGC: Count signups per product for the summary badges
  const counts = useMemo(() => ({
    farmproweather: entries.filter(e => e.product === 'farmproweather').length,
    sprayguard: entries.filter(e => e.product === 'sprayguard').length,
  }), [entries])

  // AGC: Export the full waitlist as a CSV file download
  const exportCSV = () => {
    const header = 'Product,Email,Name,Date'
    const rows = entries.map(e => {
      const date = e.createdAt?.toDate?.()?.toISOString?.() || ''
      return `${e.product},${e.email},${e.userName},${date}`
    })
    const csv = [header, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (loading) return <AdminLoadingState />

  return (
    <div>
      <AdminPageHeader
        title={t('admin.waitlist')}
        subtitle={`${entries.length} signups`}
      />

      <div className="flex items-center gap-3 mb-6">
        <AdminBadge variant="info">FarmProWeather: {counts.farmproweather}</AdminBadge>
        <AdminBadge variant="success">SprayGuard: {counts.sprayguard}</AdminBadge>
        <div className="flex-1" />
        <AdminButton variant="secondary" onClick={exportCSV}>
          <Download className="w-4 h-4 mr-1.5" />
          {t('admin.waitlistExport')}
        </AdminButton>
      </div>

      {entries.length === 0 ? (
        <AdminEmptyState title={t('admin.waitlist')} description="No signups yet" />
      ) : (
        <AdminTable>
          <AdminThead>
            <tr>
              <AdminTh>Product</AdminTh>
              <AdminTh>Email</AdminTh>
              <AdminTh>Name</AdminTh>
              <AdminTh>Date</AdminTh>
            </tr>
          </AdminThead>
          <tbody>
            {entries.map(entry => (
              <AdminTr key={entry.id}>
                <AdminTd>
                  <AdminBadge variant={entry.product === 'farmproweather' ? 'info' : 'success'}>
                    {entry.product === 'farmproweather' ? 'FarmProWeather' : 'SprayGuard'}
                  </AdminBadge>
                </AdminTd>
                <AdminTd>{entry.email}</AdminTd>
                <AdminTd>{entry.userName || '—'}</AdminTd>
                <AdminTd>
                  {entry.createdAt?.toDate?.()?.toLocaleDateString?.() || '—'}
                </AdminTd>
              </AdminTr>
            ))}
          </tbody>
        </AdminTable>
      )}
    </div>
  )
}
